import { z } from 'zod';
import { DecimalString, IsoDate, IsoDateTime, MoneyAmount, UuidString } from '../../primitives.js';

/**
 * Task-module — **универсальный координационный слой** для управления работой людей
 * в STAITER. Заменяет legacy S17-S22 manufacturing/supply project (см. BE-S12.1-17).
 *
 * Project — контейнер для задач. `kind` определяет источник и поведение:
 *   - free               — свободный проект без привязки
 *   - work_order_execution — авто-создаётся при release Work Order, job_cards → tasks
 *   - period_close       — закрытие отчётного периода (из шаблона)
 *   - onboarding_client  — ввод нового клиента в работу
 *   - sku_launch         — запуск новой SKU
 *   - warehouse_opening  — открытие склада
 *   - integration_setup  — подключение интеграции
 *   - audit_prep         — подготовка к аудиту
 *   - tax_filing         — налоговая подача (ЭСФ/КПН)
 *   - physical_count     — инвентаризация
 *   - budget_cycle       — бюджетный цикл
 *   - production_sprint  — производственная программа на смену/неделю
 *
 * См. `docs/STAITER_UX.md` §11.2 и `docs/backlogs/task-module/be-backlog.md` BE-S12.1-1.
 */

export const ProjectKind = z.enum([
  'free',
  'work_order_execution',
  'period_close',
  'onboarding_client',
  'sku_launch',
  'warehouse_opening',
  'integration_setup',
  'audit_prep',
  'tax_filing',
  'physical_count',
  'budget_cycle',
  'production_sprint',
]);
export type ProjectKind = z.infer<typeof ProjectKind>;

/**
 * Жизнь записи проекта.
 *
 * B1 (project-actions-parity §1.1) — добавлен `cancelled`. До него «отменён» не
 * существовал как значение вовсе: отмена писала `archived`, то есть ровно то же,
 * что архивация, и различить два исхода в данных было нельзя. `archived` оставлен —
 * его несут старые строки, — но новым кодом не пишется: за видимость отвечает
 * `archived_at`, за исход — этот enum. Две оси, не одна.
 */
export const ProjectStatus = z.enum([
  'planning',
  'active',
  'on_hold',
  'completed',
  'cancelled',
  'archived',
]);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

/**
 * project-visibility (SOLUTION §0) — 'open' виден всей компании; 'private' — только
 * участники (task_module_project_members) + admin-класс. НЕ пересекается со `status`.
 */
export const ProjectVisibility = z.enum(['open', 'private']);
export type ProjectVisibility = z.infer<typeof ProjectVisibility>;

/**
 * ─── B1 (projects→task-module merge) — PM/финанс-аппарат ───
 * Канонические enum'ы PM-слоя проекта. Переезжают из legacy `entities/project.ts`
 * (ретайрится в F5/B11). `status` (выше) живёт параллельно: `status` = жизнь записи,
 * `stage` = PM-lifecycle с Tier-2 гейтами (B2).
 */
export const ProjectStage = z.enum(['planning', 'active', 'on_hold', 'completed', 'closed']);
export type ProjectStage = z.infer<typeof ProjectStage>;

/**
 * Исход записи, выведенный из PM-стадии.
 *
 * B8 (project-actions-parity §1.2) — переехало из `api/task-module-projects.ts` в
 * shared, потому что появился второй потребитель: диалог разотмены обязан назвать
 * состояние, в которое проект вернётся, ДО подтверждения. Пока правило жило только
 * на сервере, фронту оставалось его угадать — а угаданное правило расходится с
 * настоящим на первой же правке.
 *
 * `cancelled` здесь не появляется и появиться не может: отмена ортогональна стадии
 * и живёт в собственной оси (SOLUTION §1.1).
 */
export function statusForStage(stage: ProjectStage): ProjectStatus {
  switch (stage) {
    case 'completed':
    case 'closed':
      return 'completed';
    case 'active':
      return 'active';
    case 'on_hold':
      return 'on_hold';
    default:
      return 'planning';
  }
}

export const ProjectHealth = z.enum(['on_track', 'at_risk', 'overdue', 'on_hold']);
export type ProjectHealth = z.infer<typeof ProjectHealth>;

export const PercentCompleteMethod = z.enum([
  'manual',
  'task_completion',
  'task_progress',
  'task_weight',
]);
export type PercentCompleteMethod = z.infer<typeof PercentCompleteMethod>;

export const ProjectType = z.enum(['supply', 'manufacturing', 'rnd', 'modernization', 'other']);
export type ProjectType = z.infer<typeof ProjectType>;

/** Типы ERP-сущностей, к которым может быть привязан проект через source_type+source_id. */
export const ProjectSourceType = z.enum([
  'work_order',
  'period_id',
  'counterparty',
  'item',
  'warehouse',
  'integration',
  'esf_filing',
  'kpn_filing',
  // Системные контейнеры без source_id (project-visibility §4.7): quick-add инбокс
  // «Входящие» и сид-инбокс автоправил. Без них Zod-parse инбокс-проекта падал.
  'quick_inbox',
  'system_inbox',
]);
export type ProjectSourceType = z.infer<typeof ProjectSourceType>;

export const Project = z
  .object({
    id: UuidString,
    /** Multi-tenant scope. */
    entity_id: UuidString,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    owner_user_id: UuidString,
    kind: ProjectKind,
    /** Polymorphic ref на ERP-сущность. Оба поля либо null, либо заданы. */
    source_type: ProjectSourceType.nullable(),
    source_id: UuidString.nullable(),
    /** Если развёрнут из шаблона — ссылка на BlueprintVersion. */
    blueprint_id: UuidString.nullable(),
    blueprint_version: z.number().int().positive().nullable(),
    /** Плановые даты (expected). Фактические — actual_* ниже. */
    start_date: IsoDate.nullable(),
    end_date: IsoDate.nullable(),
    status: ProjectStatus,
    /** open (вся компания) | private (только участники + admin). SOLUTION §0. */
    visibility: ProjectVisibility.default('open'),
    /** user_ids участников (через through-таблицу project_members). */
    members: z.array(UuidString),
    created_at: IsoDateTime,
    /** Soft-delete (30d retention; для kind=period_close — до закрытия периода). */
    archived_at: IsoDateTime.nullable(),
    // ─── B1 — PM/финанс-поля (nullable: существующие tm-проекты их не имеют) ───
    /** Server-generated `PROJ-YYYY-####`. */
    code: z.string().min(1).nullable().default(null),
    project_type: ProjectType.nullable().default(null),
    /** PM-lifecycle (отдельно от `status`); Tier-2 переходы — B2. */
    stage: ProjectStage.nullable().default(null),
    health: ProjectHealth.nullable().default(null),
    customer_id: UuidString.nullable().default(null),
    /** JOIN enrichment. */
    customer_name: z.string().nullable().optional(),
    sales_order_id: UuidString.nullable().default(null),
    sales_order_code: z.string().nullable().optional(),
    pm_user_id: UuidString.nullable().default(null),
    pm_user_name: z.string().nullable().optional(),
    notes: z.string().nullable().default(null),
    /** Temporal lifecycle workflow id (legacy projects.workflow_id). */
    pm_workflow_id: z.string().nullable().optional(),
    /** Computed (P8): earliest task_log / WO start. */
    actual_start_date: IsoDateTime.nullable().default(null),
    actual_end_date: IsoDateTime.nullable().default(null),
    /**
     * B26 (PPC-F6) — кто, когда и почему отменил. Optional: вкладка, открытая до
     * выката поля, не падает; у проектов, отменённых до 0127 без события аудита, — null.
     */
    cancelled_at: IsoDateTime.nullable().optional(),
    cancelled_by: UuidString.nullable().optional(),
    cancel_reason: z.string().nullable().optional(),
    /** PPC-F6 (F17) — имя автора отмены (полное имя или почта); только в карточке. */
    cancelled_by_name: z.string().nullable().optional(),
    /**
     * B9 (project-plan-change §1.1) — базовая линия плана: исходное обещание на
     * момент выхода из планирования. Третье измерение рядом с планом и фактом.
     *
     * Nullable по устройству: у проектов в `planning` снимка ещё нет, а у ушедших в
     * работу без дат он есть, но пустой (`plan_baseline_set` при пустых датах).
     */
    baseline_start_date: IsoDate.nullable().default(null),
    baseline_end_date: IsoDate.nullable().default(null),
    /**
     * Снимок СНЯТ (даже если сам план был пуст). Отличает «обещания не было» от
     * «обещание было пустым» — от этого зависит основание `first_baseline` (§8.2-О2).
     */
    plan_baseline_set: z.boolean().default(false),
    /**
     * Сдвиг плана в календарных днях: `end_date − baseline_end_date`.
     *
     * Считает СЕРВЕР, не фронт: одно правило на всех потребителей. `null` (а не 0)
     * когда сравнивать не с чем — у проекта без базовой линии или без даты конца.
     * Ноль и «не с чем сравнивать» — разные вещи, и экран обязан их различать.
     */
    plan_shift_days: z.number().int().nullable().default(null),
    percent_complete: DecimalString.default('0'),
    percent_complete_method: PercentCompleteMethod.default('task_progress'),
    /** Financial baseline (user input). */
    estimated_cost: MoneyAmount.optional(),
    currency: z.string().optional(),
    /** Denormalized (P12.3 recomputeProjectMetrics, см. B4). */
    total_cost_labor: MoneyAmount.optional(),
    total_cost_materials: MoneyAmount.optional(),
    total_cost_purchase: MoneyAmount.optional(),
    total_cost_landed: MoneyAmount.optional(),
    total_cost_actual: MoneyAmount.optional(),
    total_billed_amount: MoneyAmount.optional(),
    gross_margin: MoneyAmount.optional(),
    per_gross_margin: DecimalString.default('0'),
  })

  .refine(
    (d) =>
      // Системные контейнеры (инбоксы) несут source_type без source_id — исключение из
      // «оба null или оба заданы» (project-visibility §4.7).
      d.source_type === 'quick_inbox' ||
      d.source_type === 'system_inbox' ||
      (d.source_type === null) === (d.source_id === null),
    {
      message: 'source_type и source_id должны быть оба null или оба заданы',
      path: ['source_id'],
    },
  )
  .refine(
    (d) => {
      const kindsRequiringSource: ProjectKind[] = ['period_close', 'work_order_execution'];
      if (kindsRequiringSource.includes(d.kind)) {
        return d.source_id !== null;
      }
      return true;
    },
    {
      message: 'kind=period_close или work_order_execution требует source_id',
      path: ['source_id'],
    },
  );
export type Project = z.infer<typeof Project>;

/**
 * BE-4 — denorm-каталог участника проекта: UUID ref + denorm имени/почты/аватара.
 *
 * Ответ `GET /projects/:id/members`. FE заполняет `users`-prop пикеров
 * ответственного (карточка/строка/канбан/гант/календарь) и список в настройках
 * без N+1. Structural-only (i18n): full_name/email/avatar_url — данные, не
 * freeform UI-текст; роль — enum-код (FE локализует). avatar_url = null (колонки
 * avatar в users нет), full_name = users.fullName (fallback email на FE).
 */
export const ProjectMember = z.object({
  project_id: UuidString,
  user_id: UuidString,
  role_in_project: z.enum(['lead', 'member', 'viewer']),
  full_name: z.string().nullable(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  added_at: IsoDateTime,
});
export type ProjectMember = z.infer<typeof ProjectMember>;

/**
 * ─── B3 — health status-update + journal ───
 * Порт legacy `ProjectStatusUpdate`. `narrative` — пользовательский ввод (as-is),
 * не machine-string; health-коды — enum (FE локализует).
 */
export const ProjectStatusUpdate = z.object({
  id: UuidString,
  project_id: UuidString,
  update_date: IsoDateTime,
  previous_health: ProjectHealth.nullable(),
  new_health: ProjectHealth,
  narrative: z.string(),
  user_id: UuidString,
  /** JOIN enrichment (email/имя автора) — для журнала. */
  user_name: z.string().nullable().optional(),
  created_at: IsoDateTime,
});
export type ProjectStatusUpdate = z.infer<typeof ProjectStatusUpdate>;

/** Вход POST /:id/status-update — смена health + обязательный нарратив. */
export const ProjectStatusUpdateInput = z
  .object({
    new_health: ProjectHealth,
    narrative: z.string().min(1).max(2000),
  })
  // zod-strict-ok: input request-body
  .strict();
export type ProjectStatusUpdateInput = z.infer<typeof ProjectStatusUpdateInput>;

/** Форма для создания проекта (без серверных полей). */
export const ProjectCreate = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().default(null),
    owner_user_id: UuidString,
    kind: ProjectKind,
    source_type: ProjectSourceType.nullable().default(null),
    source_id: UuidString.nullable().default(null),
    blueprint_id: UuidString.nullable().default(null),
    blueprint_version: z.number().int().positive().nullable().default(null),
    start_date: IsoDate.nullable().default(null),
    end_date: IsoDate.nullable().default(null),
    status: ProjectStatus.default('planning'),
    /** open (дефолт) | private. Свитчер в форме создания (F1). SOLUTION §2.3. */
    visibility: ProjectVisibility.default('open'),
    members: z.array(UuidString).default([]),
  })

  .refine(
    (d) =>
      // Системные контейнеры (инбоксы) несут source_type без source_id — исключение из
      // «оба null или оба заданы» (project-visibility §4.7).
      d.source_type === 'quick_inbox' ||
      d.source_type === 'system_inbox' ||
      (d.source_type === null) === (d.source_id === null),
    {
      message: 'source_type и source_id должны быть оба null или оба заданы',
      path: ['source_id'],
    },
  );
export type ProjectCreate = z.infer<typeof ProjectCreate>;

/** Форма для частичного обновления (PATCH). */
export const ProjectPatch = Project.innerType()
  .innerType()
  .omit({ id: true, entity_id: true, created_at: true })
  .partial();
export type ProjectPatch = z.infer<typeof ProjectPatch>;

/** project-visibility (B20) — причина авто-добавления участника при open→private. */
export const VisibilityAutoAddReason = z.enum([
  'task_assignee',
  'claimed_assignee',
  'draft_author',
  'project_owner',
]);
export type VisibilityAutoAddReason = z.infer<typeof VisibilityAutoAddReason>;

/** project-visibility (B20) — данные для member-review диалога перехода open→private. */
export const VisibilityTransitionPreview = z.object({
  /** текущее число участников проекта. */
  member_count: z.number().int(),
  /** активных сотрудников компании (для баннера «в участниках вся компания»). */
  company_active_user_count: z.number().int(),
  /** кандидаты на авто-добавление (структурно; FE локализует reason). */
  auto_add_candidates: z.array(
    z.object({
      user_id: UuidString,
      full_name: z.string().nullable(),
      email: z.string().nullable(),
      reason: VisibilityAutoAddReason,
    }),
  ),
  /** число ролевых/групповых очередей без взявшего (мертвы для не-участников). */
  role_queue_count: z.number().int(),
});
export type VisibilityTransitionPreview = z.infer<typeof VisibilityTransitionPreview>;

/** project-visibility (B20) — вход перехода видимости; exclude — снятые в диалоге галки. */
export const VisibilityTransitionInput = z
  .object({
    visibility: ProjectVisibility,
    exclude_user_ids: z.array(UuidString).default([]),
  })
  // zod-strict-ok: input request-body
  .strict();
export type VisibilityTransitionInput = z.infer<typeof VisibilityTransitionInput>;

/**
 * B25 пака `tails-closeout` (TPL-F9) — превью дубликата: сколько ЧУЖИХ проектов
 * затронет опция «Перепривязать связанные задачи». Считается по членствам
 * (`task_module_task_projects`), а не по денорму: вторичные члены источника и
 * distinct их основных проектов.
 */
export const DuplicateProjectPreview = z
  .object({
    secondary_tasks_count: z.number().int().nonnegative(),
    affected_projects_count: z.number().int().nonnegative(),
  })
  .passthrough();
export type DuplicateProjectPreview = z.infer<typeof DuplicateProjectPreview>;
